/**
 * The span inspector (UX-MAP-10): every span covering the active point, grouped by channel, with the feature label,
 * weight and the channel's flagged stretches that contain the point, and the two actions per channel. A right panel on
 * wide screens and a bottom sheet below 1,200 px (CSS). All text is rendered as text nodes.
 */
import type { RefObject } from 'preact';
import { CHANNEL_IDS } from '../../engine/channels/types.ts';
import type { ChannelId } from '../../engine/channels/types.ts';
import type { ViewModel } from '../../shared/viewmodel.ts';
import { Term } from '../components/Term.tsx';
import { ChannelTerm } from '../components/Term.tsx';
import { addFocus, focus } from '../focus.ts';
import { fmt1, fmtInt, fmtInterval } from '../format.ts';
import { hashFor } from '../router.ts';
import { INSPECTOR } from '../strings-reader.ts';
import { entriesOf } from './flagged.ts';

export interface ActivePoint {
	p: number;
	/** The piece's character range. */
	a: number;
	b: number;
	/** Every span covering the piece's first character, ascending. */
	spans: number[];
}

/** Spans covering character `pos` within paragraph `p` (own and reaching-in), ascending. */
export function spansAt(vm: ViewModel, spans: readonly number[], pos: number): number[] {
	const s = vm.spans;
	return spans
		.filter((i) => (s.start[i] as number) <= pos && (s.end[i] as number) > pos)
		.sort((x, y) => x - y);
}

export function fmtWeight(w: number): string {
	return String(Number(w.toFixed(2)));
}

const KIND_WORD = {
	segment: INSPECTOR.kindSegment,
	spike: INSPECTOR.kindSpike,
	unit: INSPECTOR.kindUnit,
} as const;

export function Inspector({
	vm,
	active,
	onClose,
	closeRef,
}: {
	vm: ViewModel;
	active: ActivePoint;
	onClose: () => void;
	closeRef: RefObject<HTMLButtonElement>;
}) {
	const focused = focus.value;
	const byChannel = new Map<number, number[]>();
	for (const i of active.spans) {
		const c = vm.spans.channel[i] as number;
		const l = byChannel.get(c);
		if (l) l.push(i);
		else byChannel.set(c, [i]);
	}
	const quoteRaw = vm.text.slice(active.a, Math.min(active.b, active.a + 140));
	const quote = active.b - active.a > 140 ? `${quoteRaw}…` : quoteRaw;
	const total = vm.paras.count;
	return (
		<aside class="rd-inspector" aria-label={INSPECTOR.label} data-testid="rd-inspector">
			<div class="rd-ins-head">
				<h2 class="rd-ins-title">{INSPECTOR.title}</h2>
				<button
					type="button"
					class="btn btn-secondary rd-ins-close"
					ref={closeRef}
					onClick={onClose}
					data-testid="rd-inspector-close"
				>
					{INSPECTOR.close}
				</button>
			</div>
			<p class="ins-quote" data-testid="rd-ins-quote">
				{quote}
			</p>
			<p class="dim">
				{INSPECTOR.where(fmtInt(active.p + 1), fmtInt(total))} ·{' '}
				{INSPECTOR.coverCount(active.spans.length, byChannel.size)}
			</p>
			{active.spans.length === 0 ? <p>{INSPECTOR.none}</p> : null}
			<ul class="ins-list">
				{[...byChannel.entries()].map(([c, list]) => {
					const id = CHANNEL_IDS[c] as ChannelId;
					const d = vm.channels[c];
					const stretches = entriesOf(vm, id).filter(
						(h) => h.paraStart <= active.p && active.p < h.paraEnd
					);
					const isFocused = focused.includes(id);
					return (
						<li class="ins-item" key={id} data-channel={id}>
							<div class="ins-ch">
								<strong class="mono">
									<ChannelTerm id={id} name={d?.name} />
								</strong>
								{isFocused ? <span class="ins-tag">{INSPECTOR.focusedTag}</span> : null}
							</div>
							<ul class="ins-spans">
								{list.map((i) => {
									const w = vm.spans.weight[i] as number;
									return (
										<li key={i} data-span={i} class="ins-span">
											<span class="ins-feature">
												{d?.features[vm.spans.feature[i] as number] ?? ''}
											</span>{' '}
											<span class="dim">
												{w > 0 ? (
													<Term k="evidence-span">{INSPECTOR.weight(fmtWeight(w))}</Term>
												) : (
													<Term k="weight-zero">{INSPECTOR.weightZero}</Term>
												)}
											</span>
										</li>
									);
								})}
							</ul>
							{d?.flagsWithheld ? (
								<p class="ins-st ins-withheld" data-testid="rd-ins-withheld">
									{INSPECTOR.withheld(d.flagsWithheld)}
								</p>
							) : (
								<div class="ins-flagged">
									{stretches.length === 0 ? (
										<p class="ins-st dim">{INSPECTOR.noFlagged}</p>
									) : (
										<>
											<p class="ins-st-h dim">{INSPECTOR.flagged}</p>
											<ul class="ins-stretches">
												{stretches.map((h) => (
													<li
														key={`${h.kind}-${h.paraStart}-${h.paraEnd}`}
														class="ins-st"
														data-flag={h.flag}
														data-kind={h.kind}
													>
														<div>
															<Term k={h.flag === 'hotspot' ? 'hotspot' : 'elevated'}>
																{h.flag}
															</Term>
															{INSPECTOR.flagRest(
																KIND_WORD[h.kind],
																fmtInt(h.paraStart + 1),
																fmtInt(h.paraEnd)
															)}
														</div>
														<div class="dim">
															{INSPECTOR.scoreLine(fmt1(h.score), fmtInterval(h.ci90))}
														</div>
														{h.notes?.map((n) => (
															<div class="ins-note" key={n}>
																{n}
															</div>
														))}
													</li>
												))}
											</ul>
										</>
									)}
								</div>
							)}
							<div class="ins-actions">
								<button
									type="button"
									class="btn btn-secondary"
									aria-disabled={isFocused ? 'true' : undefined}
									data-testid="rd-ins-focus"
									onClick={() => {
										if (!isFocused) addFocus(id);
									}}
								>
									{isFocused ? INSPECTOR.focused : INSPECTOR.focusThis}
								</button>
								<a
									class="btn btn-secondary"
									href={hashFor('channel', { id })}
									data-testid="rd-ins-open"
								>
									{INSPECTOR.openChannel}
								</a>
							</div>
						</li>
					);
				})}
			</ul>
		</aside>
	);
}
